import AutomasPeritajeEntity, { AutomasAxleMeasureEntity } from "../../../../domain/entities/AutomasPeritajeEntity";
import {
    Asegurable,
    asegurabilidad,
    DASH,
    fmtCurrency,
    fmtDate,
    fmtDateSlash,
    fmtNumber,
    isEmpty,
    maskPlate,
    normalize,
    oculto,
    sentenceCase,
    text,
    titleCase,
    toNumber,
    Tono,
    toneFromPercent,
} from "./utils";

const ICON_BASE = "/assets/peritaje";

/**
 * Nombre del tipo de vehículo, tal como lo llama la API de la web (`GET /type-cars/`).
 *
 * El certificado no guarda copias de esas siluetas: resuelve aquí el *nombre* y el componente
 * saca la imagen del `TypeVehicleContext`, el mismo que usa el detalle del vehículo.
 *
 * Se busca por el primer patrón que coincida sobre `body_type`, `car_class` y `type` juntos.
 * "Sedan" es el respaldo porque "AUTOMOVIL" es lo que más manda Automás.
 */
const TIPOS_VEHICULO: { match: RegExp; nombre: string }[] = [
    { match: /MOTOCICLETA|MOTO\b|CUATRIMOTO/, nombre: "Motocicletas" },
    { match: /PICK.?UP|PLATON|ESTACAS/, nombre: "Pickup" },
    { match: /VAN\b|MICROBUS|FURGON|BUSETA|\bBUS\b/, nombre: "Vans" },
    { match: /CAMPERO|TODOTERRENO|4X4|\bSUV\b/, nombre: "SUV" },
    { match: /CAMIONETA/, nombre: "Camioneta - SUV" },
    { match: /COUPE|CUPE/, nombre: "Coupe" },
    { match: /HATCHBACK|\bHB\b/, nombre: "Hatchback" },
    { match: /SEDAN|AUTOMOVIL/, nombre: "Sedan" },
];

const tipoVehiculoDe = (data: AutomasPeritajeEntity): string => {
    const texto = normalize([data.body_type, data.car_class, data.type].map((v) => text(v)).join(" "));
    return TIPOS_VEHICULO.find((tipo) => tipo.match.test(texto))?.nombre ?? "Sedan";
};

/** Fotos destacadas de la hoja 1 (nodos 3755:7808 / 7809 / 7810). */
export const HERO_PHOTOS = 3;

export interface MedicionVM {
    label: string;
    /** Porcentaje 0-100, o `null` cuando el peritaje no trae la medición. */
    percentage: number | null;
    tono: Tono;
}

export interface CategoriaVM {
    label: string;
    icono: string;
    /** Cuántos hallazgos reportó Automás en esta categoría. */
    hallazgos: number;
    /** Severidad del peor hallazgo. `good` cuando no hay ninguno. */
    tono: Tono;
    /** "SIN NOVEDADES" / "7 NOVEDADES". */
    resumen: string;
    /** Desglose por severidad ("5 requieren atención · 2 leves"), o la leyenda de sin novedades. */
    detalle: string;
}

/** Un hallazgo suelto: la pieza y en qué estado la encontró Automás. */
export interface NovedadVM {
    pieza: string;
    estado: string;
    tono: Tono;
}

export interface GrupoNovedadesVM {
    titulo: string;
    items: NovedadVM[];
}

export interface TarjetaVM {
    titulo: string;
    descripcion: string;
    filas: MedicionVM[];
}

export interface AccesorioVM {
    cantidad: string;
    descripcion: string;
    marca: string;
    valor: string;
}

export interface ValorVM {
    titulo: string;
    subtitulo: string;
    valor: string;
}

export interface CampoVM {
    label: string;
    value: string;
}

export interface PeritajeVM {
    acta: string;
    fecha: string;
    placa: string;
    titulo: string;
    /** Nombre del tipo de vehículo; el componente lo resuelve contra `TypeVehicleContext`. */
    tipoVehiculo: string;
    resultado: string;
    /** Asegurabilidad del vehículo. `desconocido` cuando la API no la reporta. */
    asegurable: Asegurable;
    resumen: string[];
    servicio: string[];
    fotos: string[];
    /** El resto de fotos, en orden. Cómo se reparten en hojas lo decide `paginarHojas.ts`. */
    anexo: string[];
    categorias: CategoriaVM[];
    /** Detalle completo de los hallazgos, agrupado por categoría. Vacío si no hay ninguno. */
    novedades: GrupoNovedadesVM[];
    totalNovedades: number;
    /** Solo las mediciones que el peritaje realmente reporta; hoy la API no envía ninguna. */
    tarjetas: TarjetaVM[];
    observaciones: string;
    accesorios: AccesorioVM[];
    valores: ValorVM[];
    tipo: string;
    detalles: CampoVM[];
}

/**
 * ── Cómo se lee `visual_inspection` ──────────────────────────────────────────
 *
 * La API NO envía un checklist calificado: envía la lista de **hallazgos**, o sea solo las
 * piezas con novedad. En `POU775` llegan 9 y en `RNU052` 18; ninguna entrada dice "esto está
 * bien". Por eso el bloque no muestra notas ni porcentajes, sino cuántas novedades tiene cada
 * categoría: es lo único que el dato permite afirmar.
 *
 * Que una categoría no aparezca se interpreta como "sin novedades" — Automás lista lo que
 * encuentra (`RNU052` reporta las fugas cuando existen).
 */

interface CategoriaDef {
    titulo: string;
    icono: string;
    match: RegExp;
    /** Orden en el que se intenta clasificar un ítem. El menor gana. */
    prioridad: number;
    /** Texto cuando la categoría no tiene ningún hallazgo. */
    sinNovedades: string;
}

/**
 * Las categorías del bloque "DIAGNÓSTICO DE INSPECCIÓN", en el orden en que se dibujan.
 *
 * Son cuatro y no cinco: el diseño incluía "Vida útil de las llantas", pero la API nunca
 * reporta llantas (ni frenado, suspensión o alineación), así que esa fila salía siempre vacía.
 *
 * La clasificación va por el **ítem**, no por el estado: "CAPOT / MAL REPINTADO" es una novedad
 * de carrocería, no de pintura. `prioridad` es distinta del orden de pintado porque un ítem como
 * "FUGA ACEITE CAJA" coincide con varias y se queda con la más específica.
 */
const CATEGORIAS: CategoriaDef[] = [
    {
        titulo: "Estructura",
        icono: `${ICON_BASE}/diag-chasis.svg`,
        match: /PARAL|CHASIS|ESTRUCTURA|LARGUERO|BASTIDOR|MONOCASCO|TORRE|COLISION|CHOQUE/,
        prioridad: 2,
        sinNovedades: "No se reportan novedades estructurales",
    },
    {
        titulo: "Carrocería - Exterior",
        icono: `${ICON_BASE}/diag-carroceria.svg`,
        match: /.*/,
        prioridad: 99,
        sinNovedades: "No se reportan novedades de carrocería",
    },
    {
        titulo: "Pintura",
        icono: `${ICON_BASE}/diag-pintura.svg`,
        match: /IMPUREZA|PIEL DE NARANJA|LIJADO|PINTURA|BRILLO|DESCASCAR|CASCARA|BARNIZ/,
        prioridad: 1,
        sinNovedades: "No se reportan novedades de pintura",
    },
    {
        titulo: "Fuga de fluidos",
        icono: `${ICON_BASE}/diag-fluidos.svg`,
        match: /FUGA|FLUIDO|ACEITE|REFRIGERANT|LIQUIDO|LUBRICANTE|HIDRAULIC/,
        prioridad: 0,
        sinNovedades: "No se reportan fugas",
    },
];

const ORDEN_CLASIFICACION = [...CATEGORIAS].sort((a, b) => a.prioridad - b.prioridad);

const categoriaDe = (item: string): CategoriaDef => {
    const value = normalize(item);
    return ORDEN_CLASIFICACION.find((categoria) => categoria.match.test(value)) ?? CATEGORIAS[1];
};

/**
 * Severidad de un hallazgo, según el vocabulario real de Automás.
 *
 * Los valores observados en producción son: SUMIDO, MALA REPARACION, DE 1 A 4 PIEZAS,
 * BUENA REPARACION, MAL REPINTADO, SI, RAYADO y BIEN REPINTADO. No es una escala de calidad
 * ("bueno/regular/malo") sino una taxonomía de defectos, y hay que leerla como tal.
 *
 * OJO con `SI`: en los ítems de fuga ("FUGA ACEITE MOTOR") significa que **sí hay fuga**, o sea
 * el peor caso. Leerlo como el "sí" de "cumple" pintaba de verde un carro con dos fugas.
 */
const SEVERIDAD: { match: RegExp; nivel: number }[] = [
    { match: /^SI$|MALA REPARACION|MAL REPINTADO|MAL ENRASE|DESCUADRE|FISURA|ROTO|QUEBRAD|FALTANTE|OXIDO/, nivel: 2 },
    { match: /^NO$|NO APLICA|BUENA REPARACION|BIEN REPINTADO|ORIGINAL/, nivel: 0 },
];

const TONO_POR_NIVEL: Record<number, Tono> = { 0: "good", 1: "fair", 2: "bad" };

/** Plural y singular de cada nivel, para el desglose de la fila del diagnóstico. */
const ETIQUETA_NIVEL: Record<number, [string, string]> = {
    2: ["requiere atención", "requieren atención"],
    1: ["leve", "leves"],
    0: ["reparada", "reparadas"],
};

/** "5 requieren atención · 5 leves · 2 reparadas". */
const desglose = (niveles: number[]): string =>
    [2, 1, 0]
        .map((nivel) => {
            const total = niveles.filter((n) => n === nivel).length;
            if (!total) return "";
            const [singular, plural] = ETIQUETA_NIVEL[nivel];
            return `${total} ${total === 1 ? singular : plural}`;
        })
        .filter(Boolean)
        .join(" · ");

/** `SI` / `NO` son la respuesta a "¿hay fuga?": se escriben como tal, no como un estado. */
const estadoLegible = (estado: any): string => {
    const value = normalize(estado);
    if (value === "SI") return "Sí";
    if (value === "NO") return "No";
    return sentenceCase(estado);
};

/** Nivel 0 = sin defecto real, 1 = leve, 2 = requiere atención. Lo desconocido se asume leve. */
const severidadDe = (estado: any): number => {
    const value = normalize(estado);
    if (!value) return 1;
    const found = SEVERIDAD.find((entry) => entry.match.test(value));
    return found ? found.nivel : 1;
};

/** El backend concatena las novedades con separadores sueltos (`; ; .;`) que hay que limpiar. */
const limpiar = (value: string): string =>
    value
        .replace(/[;·]\s*(?=[;·.])/g, "")
        .replace(/^[\s;.·-]+/, "")
        .replace(/\s{2,}/g, " ")
        .trim();

const unir = (partes: (string | null | undefined)[], separador = " · "): string =>
    partes
        .map((parte) => limpiar(text(parte)))
        .filter((parte) => parte.length > 0)
        .join(separador);

interface HallazgosVM {
    categorias: CategoriaVM[];
    novedades: GrupoNovedadesVM[];
    total: number;
}

/**
 * Clasifica los hallazgos de `visual_inspection` en las cuatro categorías y produce, de una
 * sola pasada, el resumen de la fila del diagnóstico y el detalle pieza por pieza.
 */
const construirHallazgos = (data: AutomasPeritajeEntity): HallazgosVM => {
    const porCategoria = new Map<string, { pieza: string; estado: string; nivel: number }[]>();

    const registrar = (titulo: string, pieza: string, estado: any) => {
        const hallazgo = { pieza, estado: estadoLegible(estado), nivel: severidadDe(estado) };
        porCategoria.set(titulo, [...(porCategoria.get(titulo) ?? []), hallazgo]);
    };

    // `inspection_groups` no lo manda la API hoy, pero si algún día llega tiene prioridad.
    if (data.inspection_groups?.length) {
        data.inspection_groups.forEach((grupo) => {
            const { titulo } = categoriaDe(grupo.title);
            grupo.items.forEach((item) => registrar(titulo, sentenceCase(item.description), item.state));
        });
    } else {
        (data.visual_inspection ?? []).forEach((entry) => {
            const { titulo } = categoriaDe(entry.item);
            registrar(titulo, sentenceCase(entry.item), entry.estado);
        });
    }

    const categorias = CATEGORIAS.map((categoria) => {
        const encontradas = porCategoria.get(categoria.titulo) ?? [];
        const hallazgos = encontradas.length;
        const peor = encontradas.reduce((max, h) => Math.max(max, h.nivel), 0);

        // Cualquier hallazgo es al menos "leve"; el rojo se reserva para el nivel 2.
        const tono: Tono = hallazgos === 0 ? "good" : peor >= 2 ? "bad" : "fair";

        return {
            label: categoria.titulo,
            icono: categoria.icono,
            hallazgos,
            tono,
            resumen: hallazgos === 0 ? "SIN NOVEDADES" : `${hallazgos} ${hallazgos === 1 ? "NOVEDAD" : "NOVEDADES"}`,
            detalle: hallazgos === 0 ? categoria.sinNovedades : desglose(encontradas.map((h) => h.nivel)),
        };
    });

    const novedades = CATEGORIAS.map((categoria) => ({
        titulo: categoria.titulo,
        items: (porCategoria.get(categoria.titulo) ?? []).map(({ pieza, estado, nivel }) => ({
            pieza,
            estado,
            tono: TONO_POR_NIVEL[nivel],
        })),
    })).filter((grupo) => grupo.items.length > 0);

    const total = novedades.reduce((suma, grupo) => suma + grupo.items.length, 0);

    return { categorias, novedades, total };
};

const fila = (label: string, valor: any): MedicionVM => {
    const percentage = toNumber(valor);
    return { label, percentage, tono: toneFromPercent(percentage) };
};

/**
 * Tarjetas de medición. Solo se devuelven las que traen al menos un valor: hoy la API no envía
 * ninguna de las tres, así que la fila entera desaparece de la hoja en vez de dibujar cajas
 * vacías. Si el backend empieza a mandarlas, reaparecen solas.
 */
const construirTarjetas = (data: AutomasPeritajeEntity): TarjetaVM[] => {
    const frenos = data.brake_efficiency;
    const suspension: AutomasAxleMeasureEntity = data.suspension ?? {};
    const alineacion: AutomasAxleMeasureEntity = data.alignment ?? {};

    const todas: TarjetaVM[] = [
        {
            titulo: "EFICIENCIA DE FRENADO",
            descripcion: "Prueba de frenado de los ejes, medido en banco.",
            filas: [
                fila("Eje delantero", frenos?.front),
                fila("Eje trasero", frenos?.rear),
                // `brake_efficiency.total` es la eficiencia del sistema, no el freno de mano: solo
                // se usa como respaldo mientras el backend no envíe `parking_brake`.
                fila("Freno de mano", data.parking_brake ?? frenos?.total),
            ],
        },
        {
            titulo: "EFICIENCIA DE SUSPENSIÓN",
            descripcion: "Cómo absorbe cada eje los golpes del camino.",
            filas: [
                fila("Eje delantero", suspension.front),
                // El Figma repite "Eje delantero" en esta tarjeta (nodo 3754:7776); es un error
                // del diseño y aquí se corrige por el eje que realmente corresponde.
                fila("Eje trasero", suspension.rear),
            ],
        },
        {
            titulo: "ALINEACIÓN",
            descripcion: "Desviación del vehículo frente al límite de 10 m/Km.",
            filas: [fila("Eje delantero", alineacion.front), fila("Eje trasero", alineacion.rear)],
        },
    ];

    return todas
        .map((tarjeta) => ({ ...tarjeta, filas: tarjeta.filas.filter((f) => f.percentage !== null) }))
        .filter((tarjeta) => tarjeta.filas.length > 0);
};

const construirFotos = (data: AutomasPeritajeEntity): string[] => {
    const vistas = new Set<string>();
    [...(data.images ?? []), ...(data.photos ?? [])].forEach((imagen) => {
        if (!isEmpty(imagen?.url)) vistas.add(String(imagen.url));
    });
    return Array.from(vistas);
};

/** "No reporta siniestros ante Fasecolda" / "Reporta siniestros ante Fasecolda". */
const leyendaSiniestros = (v: AutomasPeritajeEntity["fasecolda_claims"]): string => {
    if (isEmpty(v) || v === false) return "No reporta siniestros ante Fasecolda";
    if (v === true) return "Reporta siniestros ante Fasecolda";
    const value = normalize(v);
    if (/^(NO|0|FALSE|NINGUNO|SIN)/.test(value)) return "No reporta siniestros ante Fasecolda";
    return `Reporta siniestros ante Fasecolda: ${text(v)}`;
};

/** Cómo se escribe la asegurabilidad en la rejilla de detalles. */
const ASEGURABLE_TEXTO: Record<Asegurable, string> = {
    si: "Sí",
    no: "No",
    desconocido: DASH,
};

/**
 * Traduce la respuesta de `get-car-automas` a lo que dibuja el certificado.
 *
 * Todo el criterio del diseño (qué campo va en qué caja, cómo se agrupa, cómo se enmascara y
 * cómo se leen los hallazgos) vive aquí, para que los componentes solo pinten y esta función se
 * pueda probar sola.
 */
export const buildPeritajeVM = (data: AutomasPeritajeEntity): PeritajeVM => {
    const fotos = construirFotos(data);
    const kilometraje = fmtNumber(data.mileage);
    const hallazgos = construirHallazgos(data);

    return {
        acta: text(data.acta) || text(data.inspection_number) || DASH,
        fecha: fmtDateSlash(data.date),
        // La placa NUNCA se muestra completa: solo su último carácter.
        placa: maskPlate(data.plate),
        titulo: unir([titleCase(data.brand), titleCase(data.type)], " "),
        tipoVehiculo: tipoVehiculoDe(data),
        resultado: sentenceCase(data.result),
        asegurable: asegurabilidad(data.insurable),
        resumen: [
            unir([titleCase(data.body_type), unir([titleCase(data.car_class), text(data.model)], " ")]),
            unir([titleCase(data.color), kilometraje === DASH ? "" : `${kilometraje} Km`]),
        ].filter((linea) => linea.length > 0),
        servicio: [
            isEmpty(data.requested_service) ? "" : `Servicio solicitado: ${sentenceCase(data.requested_service)}`,
            leyendaSiniestros(data.fasecolda_claims),
        ].filter((linea) => linea.length > 0),
        fotos: fotos.slice(0, HERO_PHOTOS),
        anexo: fotos.slice(HERO_PHOTOS),
        categorias: hallazgos.categorias,
        novedades: hallazgos.novedades,
        totalNovedades: hallazgos.total,
        tarjetas: construirTarjetas(data),
        observaciones: unir(
            (data.observations ?? []).map((observacion) => {
                // "RESULTADO" es el cajón de sastre de la API: como etiqueta no aporta nada.
                const tipo = normalize(observacion.tipo);
                const descripcion = limpiar(text(observacion.descripcion));
                if (!descripcion) return "";
                return !tipo || tipo === "RESULTADO" ? descripcion : `${text(observacion.tipo)}: ${descripcion}`;
            }),
            ". ",
        ),
        accesorios: (data.accessories ?? []).map((accesorio) => ({
            cantidad: fmtNumber(accesorio.quantity, "1"),
            descripcion: titleCase(accesorio.description) || DASH,
            marca: titleCase(accesorio.brand) || DASH,
            valor: fmtCurrency(accesorio.value, "$ 0"),
        })),
        valores: [
            { titulo: "VALOR AUTOMÁS", subtitulo: "Avalúo del peritaje", valor: fmtCurrency(data.author_value, "$ 0") },
            {
                titulo: "VALOR FASECOLDA",
                subtitulo: "Referencia oficial de mercado",
                valor: fmtCurrency(data.fasecolda_value, "$ 0"),
            },
            {
                titulo: "VALOR ACCESORIOS",
                subtitulo: "Suma de los accesorios registrados",
                valor: fmtCurrency(data.accessory_value, "$ 0"),
            },
            {
                titulo: "VALOR MERCADO",
                subtitulo: toNumber(data.market_value) ? "Valor comercial estimado" : "No reportado en este peritaje",
                valor: fmtCurrency(data.market_value, "$ 0"),
            },
        ],
        tipo: normalize(unir([text(data.type), text(data.engine_capacity)], " / ")) || DASH,
        detalles: [
            // Igual que arriba: la placa va enmascarada siempre.
            { label: "PLACA", value: maskPlate(data.plate) },
            { label: "MARCA", value: text(data.brand, DASH) },
            { label: "MODELO", value: text(data.model, DASH) },
            { label: "CLASE", value: titleCase(data.car_class) || DASH },
            { label: "CARROCERÍA", value: titleCase(data.body_type) || DASH },
            { label: "COLOR", value: titleCase(data.color) || DASH },
            { label: "NACIONALIDAD", value: titleCase(data.nationality) || DASH },
            // Identificadores del vehículo: se confirma que existen, no se revelan.
            { label: "N° CHASIS", value: oculto(data.chassis_number) },
            { label: "N° SERIAL", value: oculto(data.serial_number) },
            { label: "N° MOTOR", value: oculto(data.engine) },
            { label: "CÓD. FASECOLDA", value: text(data.fasecolda_code, DASH) },
            { label: "CILINDRAJE", value: isEmpty(data.engine_capacity) ? DASH : `${text(data.engine_capacity)} cc` },
            { label: "TIPO CAJA", value: titleCase(data.gearbox_type) || DASH },
            { label: "COMBUSTIBLE", value: titleCase(data.fuel_type) || DASH },
            { label: "TIPO PINTURA", value: titleCase(data.paint_type) || DASH },
            { label: "SERVICIO", value: titleCase(data.service_type) || DASH },
            { label: "KILOMETRAJE", value: kilometraje === DASH ? DASH : `${kilometraje} km` },
            { label: "CLAVE", value: text(data.key, DASH) },
            { label: "N° INSPECCIÓN", value: text(data.inspection_number, DASH) },
            { label: "FECHA DE INSPECCIÓN", value: fmtDate(data.date) },
            { label: "CENTRO INSPECCIÓN", value: titleCase(data.inspection_center) || DASH },
            { label: "TURNO", value: text(data.turn, DASH) },
            { label: "RESULTADO", value: sentenceCase(data.result) || DASH },
            { label: "SERVICIO SOLICITADO", value: sentenceCase(data.requested_service) || DASH },
            { label: "N° DE SERVICIO", value: text(data.service_number, DASH) },
            { label: "SOLICITADO POR", value: titleCase(data.requested_by) || DASH },
            // Datos del titular: no se publican en el certificado.
            { label: "CLIENTE", value: oculto(data.client) },
            { label: "IDENTIFICACIÓN", value: oculto(data.identification) },
            { label: "ASEGURADORA", value: text(data.insurance_company, DASH) },
            { label: "ASEGURABLE", value: ASEGURABLE_TEXTO[asegurabilidad(data.insurable)] },
            { label: "INTERMEDIARIO", value: text(data.intermediary, DASH) },
            { label: "SUCURSAL", value: text(data.branch, DASH) },
        ],
    };
};
